import { Component, OnInit, ChangeDetectorRef, ViewChild, OnDestroy } from '@angular/core';
import { Location } from '@angular/common';
import { Router } from '@angular/router';
import { NgModel } from '@angular/forms';
import { Observable, timer } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap, takeWhile, tap } from 'rxjs/operators';
import {
  InspectionService,
  LocalEntityService,
  MachineFamillyService,
  PeopleService
} from '../../shared/services/index';
import {
  ApplicationSubType,
  Inspection,
  InspectionFilter,
  InspectionFilterType,
  InspectionOrderField,
  Person,
  Search,
  SearchFilter
} from '../../shared/models/index';
import { BasePageComponent } from '../../base-page.component';
import { ManagerInspectionListMenuComponent } from '../../menus/manager-inspection-list.menu';
import { ModalComponent } from '../../modal/modal.component';
import { InspectionFilterModalComponent } from '../../shared/components';
import * as _ from 'lodash';
import { ApplicationType } from '../../utils';

@Component({
  templateUrl: './admin-list.component.html',
  standalone: false
})
export class ManagerInspectionListPageComponent extends BasePageComponent implements OnInit, OnDestroy {
  readonly pageSize = 50;
  readonly defaultFilters: SearchFilter[] = [
    { type: InspectionFilterType.contentType, value: '100' },
    {
      type: InspectionFilterType.applicationSubType,
      value: this.isIntallation ? `${ApplicationSubType.ParentInstallation}` : ''
    }
  ];
  filter: InspectionFilter = {};
  page = 0;
  filteredInspections: Inspection[];
  creatingInspections: Inspection[];
  search: Search;
  people: Person[];
  productLines: any[];
  statusList: number[];
  localEntities: any[];
  equipmentTypes: any[];
  loading: boolean = true;
  isListComplete = false;
  alive = true;
  @ViewChild(NgModel, { static: false }) set searchInput(value: NgModel) {
    setTimeout(() =>
      value?.valueChanges.pipe(debounceTime(500), distinctUntilChanged()).subscribe(() => this.applyFilters())
    );
  }
  @ViewChild('filterModal', { static: true }) filterModal: ModalComponent;

  constructor(
    public inspectionService: InspectionService,
    private router: Router,
    location: Location,
    private cdf: ChangeDetectorRef,
    private peopleService: PeopleService,
    private machineFamillyService: MachineFamillyService,
    private localEntityService: LocalEntityService
  ) {
    super(location);
  }

  viewInspectionInfo(inspection: Inspection): void {
    this.router.navigate([this.inspectionsRoute, inspection.id, 'view', this.inspectionsRoute]);
  }

  openInspection(inspection: Inspection): void {
    if (inspection.applicationSubType === ApplicationSubType.ParentInstallation) {
      this.router.navigate([this.inspectionsRoute, inspection.id, 'inspections']);
      return;
    }

    this.router.navigate([this.inspectionsRoute, inspection.id]);
  }

  ngOnInit(): void {
    this.initInspections();
    this.initData();
  }

  initInspections() {
    if (this.inspectionService.filter) {
      this.filter = this.inspectionService.filter;
    }
    this.getInspections().subscribe((inspections) => (this.filteredInspections = inspections));

    timer(0, 10000)
      .pipe(
        takeWhile(() => this.alive),
        switchMap(() =>
          this.inspectionService.getCreatingInspections({
            creatingInspections: this.creatingInspections?.map((i) => i.id) ?? [],
            search: {
              page: 0,
              pageSize: 1000,
              filters: [
                { type: InspectionFilterType.personal, value: '100' },
                { type: InspectionFilterType.status, value: `-50` },
                {
                  type: InspectionFilterType.applicationSubType,
                  value: this.isIntallation ? `${ApplicationSubType.ParentInstallation}` : ''
                }
              ]
            }
          })
        )
      )
      .subscribe((inspections) => {
        inspections.filter((i) => i.status >= 0).forEach((i) => (i.isNew = true));
        this.creatingInspections = inspections;
        if (!this.creatingInspections?.filter((i) => i.status === -50).length) this.alive = false;
      });
  }

  sort(orderBy: InspectionOrderField) {
    this.resetPagination();
    this.filter.order = orderBy;
    this.filteredInspections = [];
    this.getInspections().subscribe((inspections) => (this.filteredInspections = inspections));
  }

  applyFilters() {
    this.resetPagination();
    this.filteredInspections = [];
    this.getInspections().subscribe((inspections) => (this.filteredInspections = inspections));
  }

  resetFilters() {
    this.showSearch = false;
    this.filter = {};
    this.applyFilters();
  }

  loadNextPage() {
    if (this.loading || this.isListComplete) return;

    this.page++;
    this.getInspections().subscribe((inspections) => {
      if (!inspections.length) {
        this.isListComplete = true;
      }
      this.filteredInspections.push(...inspections);
    });
  }

  exportInspections() {
    this.inspectionService.exportInspections({ ...this.search, page: 0, pageSize: 1000000 });
  }

  public openMenu(): void {
    this.modal.modalFooter = false;
    this.modal.modalHeader = false;
    this.modal.modalMessage = false;
    this.modal.open(ManagerInspectionListMenuComponent);
  }

  openFilters(): void {
    this.filterModal.hasClassModal = true;
    this.filterModal.open(InspectionFilterModalComponent, {
      filter: this.filter,
      people: this.people,
      productLines: this.productLines,
      statusList: this.statusList,
      localEntities: this.localEntities,
      equipmentTypes: this.equipmentTypes
    });
  }

  public selectMenu(item: any) {
    switch (item) {
      case 1:
        this.gotoCreateEmptyInspectionPage();
        break;
      case 3:
        this.gotoCreateInspectionModelPage();
        break;
      default:
    }
  }

  public gotoCreateEmptyInspectionPage() {
    return this.router.navigate([this.inspectionsRoute + 'new', false]);
  }

  public newInspectionFromModel() {
    return this.router.navigate([this.inspectionsRoute, 'selectMachine']);
  }

  public gotoCreateInspectionModelPage() {
    return this.router.navigate([this.inspectionsRoute + '/new', true]);
  }

  viewReport(inspection: Inspection) {
    this.inspectionService.viewReport(inspection.reportItemId);
  }

  refreshPage() {
    this.refresh();
  }

  goToBack(): void {
    this.router.navigate([this.appRoute]);
  }

  get inspections() {
    if (!this.creatingInspections && !this.filteredInspections) return null;
    return [...(this.creatingInspections ?? []), ...(this.filteredInspections ?? [])];
  }

  get inspectionsLabel() {
    return `home.inspection-${this.currentAppType}-list`;
  }

  get newInspectionLabel() {
    return `inspections.add-${this.currentAppType}-new`;
  }

  get isIntallation() {
    return this.currentAppType === ApplicationType.Installation;
  }

  get filtersApplied() {
    return !!Object.keys(this.filter).length;
  }

  private initData() {
    this.peopleService.getPeople().subscribe((people) => (this.people = _.sortBy(people, ['name'])));
    this.machineFamillyService
      .getCurrentScopeProductLines()
      .subscribe((productLines) => (this.productLines = productLines));
    this.localEntityService.getLocalEntities().subscribe((localEntities) => (this.localEntities = localEntities));
    this.machineFamillyService.getModels().subscribe((models) => (this.equipmentTypes = models));
    this.statusList = this.inspectionService.getStatusList();
  }

  private getInspections(): Observable<Inspection[]> {
    this.processFilters();
    this.loading = true;
    return this.inspectionService.getFilteredInspections(this.search).pipe(
      tap(() => {
        this.loading = false;
        this.inspectionService.filter = this.filter;
      })
    );
  }

  private processFilters() {
    const filters: SearchFilter[] = [];
    for (const key in this.filter) {
      const value = this.filter[key];
      if (value) {
        filters.push({ type: InspectionFilterType[key], value });
      }
    }
    this.search = {
      page: this.page,
      pageSize: this.pageSize,
      filters: [...this.defaultFilters, ...filters],
      minimumEtag: null
    };
  }

  private resetPagination() {
    this.page = 0;
    this.isListComplete = false;
  }

  ngOnDestroy() {
    this.alive = false;
  }
}
